import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../components/interface/Config";
import { apiHandler } from "../../services/authService";
import { 
  Dna, 
  Zap, 
  Pill, 
  Thermometer, 
  Stethoscope, 
  BookOpen, 
  Beaker, 
  Monitor, 
  Clipboard, 
  Info,
  ChevronRight,
  Loader2
} from "lucide-react";
import { motion } from "framer-motion";

interface ProviderInfo {
  id: number;
  user_id: number;
  hospital_id: number;
  group_id: number;
}

const Masters: React.FC = () => {
  const navigate = useNavigate();
  const [providerInfo, setProviderInfo] = useState<ProviderInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchProviderInfo = async () => {
      try {
        const url = `${BASE_URL}/users/providerinfo?user_id=undefined`;
        const response = await apiHandler('', 'GET', {}, url);
        if (response?.data) {
          setProviderInfo(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching provider info:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProviderInfo();
  }, []);

  const cardItems = [
    { 
      title: "Allergy", 
      subtitle: "Manage patient allergies", 
      icon: <Dna size={28} />, 
      hoverBorder: "hover:border-rose-200 hover:shadow-xl hover:shadow-rose-500/5",
      iconStyle: "bg-rose-50 text-rose-500 group-hover:bg-rose-500 group-hover:text-white",
      glowBg: "bg-rose-500/5",
      textColor: "text-rose-500 group-hover:text-rose-600"
    },
    { 
      title: "Allergy Reaction", 
      subtitle: "Track severity and symptoms", 
      icon: <Zap size={28} />, 
      hoverBorder: "hover:border-amber-200 hover:shadow-xl hover:shadow-amber-500/5",
      iconStyle: "bg-amber-50 text-amber-600 group-hover:bg-amber-500 group-hover:text-white",
      glowBg: "bg-amber-500/5",
      textColor: "text-amber-500 group-hover:text-amber-600"
    },
    { 
      title: "Medication (Generic)", 
      subtitle: "Database of generic drugs", 
      icon: <Pill size={28} />, 
      hoverBorder: "hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/5",
      iconStyle: "bg-emerald-50 text-emerald-500 group-hover:bg-emerald-500 group-hover:text-white",
      glowBg: "bg-emerald-500/5",
      textColor: "text-emerald-500 group-hover:text-emerald-600"
    },
    { 
      title: "Medication (Brand)", 
      subtitle: "Branded pharmaceutical records", 
      icon: <Pill size={28} />, 
      hoverBorder: "hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5",
      iconStyle: "bg-blue-50 text-blue-500 group-hover:bg-blue-500 group-hover:text-white",
      glowBg: "bg-blue-500/5",
      textColor: "text-blue-500 group-hover:text-blue-600"
    },
    { 
      title: "Medication (Sig Info)", 
      subtitle: "Dosage and frequency data", 
      icon: <Info size={28} />, 
      hoverBorder: "hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-500/5",
      iconStyle: "bg-indigo-50 text-indigo-500 group-hover:bg-indigo-500 group-hover:text-white",
      glowBg: "bg-indigo-500/5",
      textColor: "text-indigo-500 group-hover:text-indigo-600"
    },
    { 
      title: "Symptoms", 
      subtitle: "Clinical symptom library", 
      icon: <Thermometer size={28} />, 
      hoverBorder: "hover:border-orange-200 hover:shadow-xl hover:shadow-orange-500/5",
      iconStyle: "bg-orange-50 text-orange-500 group-hover:bg-orange-500 group-hover:text-white",
      glowBg: "bg-orange-500/5",
      textColor: "text-orange-500 group-hover:text-orange-600"
    },
    { 
      title: "Condition", 
      subtitle: "Medical conditions and diagnosis", 
      icon: <Stethoscope size={28} />, 
      hoverBorder: "hover:border-violet-200 hover:shadow-xl hover:shadow-violet-500/5",
      iconStyle: "bg-violet-50 text-violet-500 group-hover:bg-violet-500 group-hover:text-white",
      glowBg: "bg-violet-500/5",
      textColor: "text-violet-500 group-hover:text-violet-600"
    },
    { 
      title: "ICD 10", 
      subtitle: "International disease codes", 
      icon: <BookOpen size={28} />, 
      hoverBorder: "hover:border-slate-300 hover:shadow-xl hover:shadow-slate-500/5",
      iconStyle: "bg-slate-100 text-slate-600 group-hover:bg-slate-600 group-hover:text-white",
      glowBg: "bg-slate-500/5",
      textColor: "text-slate-500 group-hover:text-slate-600"
    },
    { 
      title: "Lab", 
      subtitle: "Laboratory test types", 
      icon: <Beaker size={28} />, 
      hoverBorder: "hover:border-cyan-200 hover:shadow-xl hover:shadow-cyan-500/5",
      iconStyle: "bg-cyan-50 text-cyan-500 group-hover:bg-cyan-500 group-hover:text-white",
      glowBg: "bg-cyan-500/5",
      textColor: "text-cyan-500 group-hover:text-cyan-600"
    },
    { 
      title: "Imaging", 
      subtitle: "Radiology and scan masters", 
      icon: <Monitor size={28} />, 
      hoverBorder: "hover:border-sky-200 hover:shadow-xl hover:shadow-sky-500/5",
      iconStyle: "bg-sky-50 text-sky-500 group-hover:bg-sky-500 group-hover:text-white",
      glowBg: "bg-sky-500/5",
      textColor: "text-sky-500 group-hover:text-sky-600"
    },
    { 
      title: "Procedure", 
      subtitle: "Medical procedure catalogs", 
      icon: <Clipboard size={28} />, 
      hoverBorder: "hover:border-teal-200 hover:shadow-xl hover:shadow-teal-500/5",
      iconStyle: "bg-teal-50 text-teal-500 group-hover:bg-teal-500 group-hover:text-white",
      glowBg: "bg-teal-500/5",
      textColor: "text-teal-500 group-hover:text-teal-600"
    },
  ];

  const handleCardClick = (title: string) => {
    const formattedTitle = title.toLowerCase().replace(/[^a-z0-9]/g, '');
    const providerId = providerInfo?.id;
    const userid = providerInfo?.user_id;
    if (providerId) {
      navigate(`/home/masters/${formattedTitle}-masters`, { state: { providerId, userid } });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
        <p className="mt-4 text-slate-500 font-medium">Loading clinical masters...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 md:px-0">
      <header className="mb-10">
        <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-2">Clinical Masters</h1>
        <p className="text-slate-500 font-medium">Configure and manage medical reference data for your practice.</p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {cardItems.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            whileHover={{ y: -6, scale: 1.02 }}
            onClick={() => handleCardClick(item.title)}
            className={`group bg-white p-6.5 rounded-[2rem] border border-slate-100 shadow-sm transition-all duration-300 cursor-pointer flex flex-col items-center text-center relative overflow-hidden ${item.hoverBorder}`}
          >
            {/* Unique Color Glow Accent */}
            <div className={`absolute top-0 right-0 w-28 h-28 ${item.glowBg} rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500`} />
            
            {/* Glowing Icon Frame */}
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 transition-all duration-300 relative z-10 shadow-sm border border-slate-100 ${item.iconStyle}`}>
              {item.icon}
            </div>
            
            <h3 className="text-slate-900 font-extrabold text-lg mb-2 transition-colors relative z-10">{item.title}</h3>
            <p className="text-slate-500 text-sm mb-6 leading-relaxed px-2 relative z-10">{item.subtitle}</p>
            
            <div className={`mt-auto flex items-center font-black text-[10px] uppercase tracking-widest gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 ${item.textColor}`}>
              Manage <ChevronRight size={12} className="stroke-[3]" />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Masters;
